import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Table,
  Button,
  Typography,
  Tag,
  Space,
  Tooltip,
  Row,
  Col,
  Statistic,
  message,
  Modal,
  Input,
  Descriptions,
  Alert,
  Empty,
  Divider,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  DollarOutlined,
  UserOutlined,
  CalendarOutlined,
  FileTextOutlined,
  BankOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import { AppContext } from "../context/AppContext";
import AccountsService from "../utils/accountsService";
import { TransactionHooks } from "../utils/transactionHooks";

const { Title, Text } = Typography;
const { TextArea } = Input;

// Zambian colors
const ZAMBIAN_COLORS = {
  green: "#078930",
  red: "#EF3340",
  black: "#000000",
  orange: "#FFB81C",
};

const ExpenseApprovalsPage = () => {
  const [pendingExpenses, setPendingExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [approvalType, setApprovalType] = useState(""); // 'approve' or 'reject'

  const { company, user, accounts, updateAccounts } = useContext(AppContext);
  const token = localStorage.getItem("token");

  const fetchPendingExpenses = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        "https://zedlenders.pickmesms.com/items/transactions",
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            filter: {
              company: company?.id,
              transaction_status: "Pending",
              transaction_type: "Operational Cost",
            },
            fields:
              "*, account.*, user_created.first_name, user_created.last_name, user_created.email",
            sort: ["-date_created"],
          },
        }
      );

      const expenses = response.data.data || [];
      console.log("Fetched pending expenses:", expenses);
      if (expenses.length > 0) {
        console.log("Sample expense structure:", expenses[0]);
        console.log("Sample account field:", expenses[0].account);
        console.log("Sample user_created field:", expenses[0].user_created);
      }
      console.log("Available accounts:", accounts);
      setPendingExpenses(expenses);
    } catch (error) {
      console.error("Error fetching pending expenses:", error);
      message.error("Failed to load pending expenses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (company?.id && user?.role === "Administrator") {
      fetchPendingExpenses();
    }
  }, [company?.id, user?.role]);

  // Check if user is administrator
  if (user?.role !== "Administrator") {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message="Access Restricted"
          description="Only administrators can access the expense approval page."
          type="error"
          showIcon
        />
      </div>
    );
  }

  const handleApproveExpense = async (expense) => {
    try {
      setProcessingId(expense.id);

      // Update transaction status to Completed
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/transactions/${expense.id}`,
        {
          transaction_status: "Completed",
          approved_by: user?.email,
          approved_date: new Date().toISOString(),
          approval_notes: "Approved by administrator",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update account balance (subtract the expense amount)
      let accountId =
        expense.account?.id || expense.account || expense.account_id;
      const account = accounts.find((acc) => acc.id === accountId);
      if (account) {
        const newBalance =
          parseFloat(account.balance || 0) - parseFloat(expense.amount || 0);

        await AccountsService.updateAccount(
          account.id,
          { balance: newBalance },
          token
        );

        // Update the transaction with the new balance
        await axios.patch(
          `https://zedlenders.pickmesms.com/items/transactions/${expense.id}`,
          {
            new_amount: newBalance,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Record double-entry accounting for expense
        try {
          await TransactionHooks.onExpenseRecorded({
            amount: parseFloat(expense.amount || 0),
            description: expense.notes || expense.description || "Operational expense",
            date: new Date(expense.transaction_date || new Date()),
            company: company?.id,
            token,
            accountId: account.id,
          });
        } catch (doubleEntryError) {
          console.error("Error recording double-entry for expense:", doubleEntryError);
          // Continue even if double-entry fails
        }
      }

      message.success("Expense approved successfully!");

      // Refresh the pending expenses list
      await fetchPendingExpenses();

      // Update accounts context to refresh navbar
      if (company?.id) {
        const updatedAccounts = await AccountsService.fetchAccounts(
          company.id,
          token
        );
        updateAccounts(updatedAccounts);
      }
    } catch (error) {
      console.error("Error approving expense:", error);
      message.error("Failed to approve expense");
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectExpense = async (expense, reason) => {
    try {
      setProcessingId(expense.id);

      // Update transaction status to Failed/Rejected
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/transactions/${expense.id}`,
        {
          transaction_status: "Failed",
          rejected_by: user?.email,
          rejected_date: new Date().toISOString(),
          rejection_reason: reason || "Rejected by administrator",
          approval_notes: reason || "Rejected by administrator",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      message.success("Expense rejected successfully!");

      // Refresh the pending expenses list
      await fetchPendingExpenses();
    } catch (error) {
      console.error("Error rejecting expense:", error);
      message.error("Failed to reject expense");
    } finally {
      setProcessingId(null);
      setApprovalModalVisible(false);
      setRejectionReason("");
      setSelectedExpense(null);
    }
  };

  const showApprovalModal = (expense, type) => {
    setSelectedExpense(expense);
    setApprovalType(type);
    setApprovalModalVisible(true);
  };

  const handleModalOk = async () => {
    if (approvalType === "approve") {
      await handleApproveExpense(selectedExpense);
      setApprovalModalVisible(false);
      setSelectedExpense(null);
    } else if (approvalType === "reject") {
      if (!rejectionReason.trim()) {
        message.error("Please provide a reason for rejection");
        return;
      }
      await handleRejectExpense(selectedExpense, rejectionReason);
    }
  };

  const columns = [
    {
      title: "Date Submitted",
      dataIndex: "date_created",
      key: "date_created",
      render: (date) => (
        <div>
          <div>{moment(date).format("DD MMM YYYY")}</div>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {moment(date).format("HH:mm")}
          </Text>
        </div>
      ),
      sorter: (a, b) =>
        moment(a.date_created).unix() - moment(b.date_created).unix(),
      defaultSortOrder: "descend",
    },
    {
      title: "Submitted By",
      dataIndex: "created_by",
      key: "created_by",
      render: (email, record) => {
        // Try to get user name from user_created field
        const userName = record.user_created
          ? `${record.user_created.first_name || ""} ${record.user_created.last_name || ""}`.trim()
          : "";

        const displayName = userName || email || "Unknown User";

        return (
          <div>
            <Space>
              <UserOutlined style={{ color: ZAMBIAN_COLORS.green }} />
              <span>{displayName}</span>
            </Space>
          </div>
        );
      },
    },

    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => (
        <Text strong style={{ color: ZAMBIAN_COLORS.red, fontSize: "16px" }}>
          ZMW {parseFloat(amount || 0).toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => parseFloat(a.amount || 0) - parseFloat(b.amount || 0),
    },
    {
      title: "Payment Method",
      dataIndex: "payment_method",
      key: "payment_method",
      render: (method) => {
        const methodLabels = {
          bank_transfer: "Bank Transfer",
          mobile_money: "Mobile Money",
          cash: "Cash",
        };
        return (
          <Tag color="blue">
            {methodLabels[method] || method?.replace(/_/g, " ") || "N/A"}
          </Tag>
        );
      },
    },
    {
      title: "Reference",
      dataIndex: "reference_number",
      key: "reference_number",
      render: (ref) => ref || "N/A",
    },
    {
      title: "Status",
      dataIndex: "transaction_status",
      key: "transaction_status",
      render: (status) => (
        <Tag color="orange" icon={<ClockCircleOutlined />}>
          {status}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Approve Expense">
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleOutlined />}
              loading={processingId === record.id}
              style={{
                backgroundColor: ZAMBIAN_COLORS.green,
                borderColor: ZAMBIAN_COLORS.green,
              }}
              onClick={() => showApprovalModal(record, "approve")}
            >
              Approve
            </Button>
          </Tooltip>
          <Tooltip title="Reject Expense">
            <Button
              danger
              size="small"
              icon={<CloseCircleOutlined />}
              loading={processingId === record.id}
              onClick={() => showApprovalModal(record, "reject")}
            >
              Reject
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Calculate summary statistics
  const totalPendingAmount = pendingExpenses.reduce(
    (sum, expense) => sum + parseFloat(expense.amount || 0),
    0
  );

  return (
    <div style={{ padding: 24 }}>
      <Card
        bordered={false}
        style={{
          borderTop: `4px solid ${ZAMBIAN_COLORS.green}`,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.09)",
        }}
      >
        <Row gutter={16} align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title
              level={3}
              style={{ color: ZAMBIAN_COLORS.green, marginBottom: 0 }}
            >
              Expense Approvals
            </Title>
            <Text type="secondary">
              Review and approve pending operational cost expenses
            </Text>
          </Col>
          <Col flex="auto" />
          <Col>
            <Button
              onClick={fetchPendingExpenses}
              loading={loading}
              style={{
                borderColor: ZAMBIAN_COLORS.green,
                color: ZAMBIAN_COLORS.green,
              }}
            >
              Refresh
            </Button>
          </Col>
        </Row>

        {/* Information Alert */}
        {/* <Alert
          message="Expense Approval System Active"
          description={
            <div>
              <p style={{ marginBottom: 8 }}>
                You are reviewing expenses that require administrator approval. 
                Currently restricted users: <strong>angelankhowani@gmail.com</strong>
              </p>
           
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          closable
        /> */}

        {/* Summary Cards */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8}>
            <Card bordered={false}>
              <Statistic
                title="Pending Expenses"
                value={pendingExpenses.length}
                prefix={<ExclamationCircleOutlined />}
                valueStyle={{ color: ZAMBIAN_COLORS.orange }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card bordered={false}>
              <Statistic
                title="Total Pending Amount"
                value={totalPendingAmount}
                prefix="ZMW"
                valueStyle={{ color: ZAMBIAN_COLORS.red }}
                formatter={(value) => value.toLocaleString()}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card bordered={false}>
              <Statistic
                title="Requires Action"
                value={pendingExpenses.length}
                valueStyle={{ color: ZAMBIAN_COLORS.black }}
                suffix="items"
              />
            </Card>
          </Col>
        </Row>

        {pendingExpenses.length === 0 ? (
          <Card bordered={false}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div>
                  <Text
                    style={{ fontSize: "16px", color: ZAMBIAN_COLORS.green }}
                  >
                    No Pending Expenses
                  </Text>
                  <br />
                  <Text type="secondary">
                    All expenses have been processed or there are no submissions
                    requiring approval.
                  </Text>
                </div>
              }
            />
          </Card>
        ) : (
          <Table
            columns={columns}
            dataSource={pendingExpenses}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1200 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} pending expenses`,
            }}
            expandable={{
              expandedRowRender: (record) => (
                <div style={{ padding: "16px", backgroundColor: "#fafafa" }}>
                  <Descriptions title="Expense Details" column={2} size="small">
                    <Descriptions.Item label="Transaction Date">
                      <Space>
                        <CalendarOutlined />
                        {moment(record.transaction_date).format("DD MMM YYYY")}
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Submitted On">
                      {moment(record.date_created).format("DD MMM YYYY HH:mm")}
                    </Descriptions.Item>
                    <Descriptions.Item label="Reference Number">
                      <Text code>{record.reference_number || "N/A"}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Payment Method">
                      <Tag>
                        {record.payment_method?.replace(/_/g, " ") || "N/A"}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Notes" span={2}>
                      <Text>
                        <FileTextOutlined style={{ marginRight: 8 }} />
                        {record.notes || "No additional notes provided"}
                      </Text>
                    </Descriptions.Item>
                  </Descriptions>
                </div>
              ),
            }}
          />
        )}
      </Card>

      {/* Approval/Rejection Modal */}
      <Modal
        title={
          approvalType === "approve" ? (
            <span>
              <CheckCircleOutlined
                style={{ color: ZAMBIAN_COLORS.green, marginRight: 8 }}
              />
              Approve Expense
            </span>
          ) : (
            <span>
              <CloseCircleOutlined
                style={{ color: ZAMBIAN_COLORS.red, marginRight: 8 }}
              />
              Reject Expense
            </span>
          )
        }
        open={approvalModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setApprovalModalVisible(false);
          setSelectedExpense(null);
          setRejectionReason("");
        }}
        okText={approvalType === "approve" ? "Approve" : "Reject"}
        okButtonProps={{
          style:
            approvalType === "approve"
              ? {
                  backgroundColor: ZAMBIAN_COLORS.green,
                  borderColor: ZAMBIAN_COLORS.green,
                }
              : { backgroundColor: ZAMBIAN_COLORS.red },
          loading: processingId === selectedExpense?.id,
        }}
        cancelButtonProps={{ loading: processingId === selectedExpense?.id }}
        width={600}
      >
        {selectedExpense && (
          <div>
            <Alert
              message={
                approvalType === "approve"
                  ? "Confirm Expense Approval"
                  : "Confirm Expense Rejection"
              }
              description={(() => {
                const userName = selectedExpense.user_created
                  ? `${selectedExpense.user_created.first_name || ""} ${selectedExpense.user_created.last_name || ""}`.trim()
                  : "";
                const submittedBy =
                  userName || selectedExpense.created_by || "Unknown User";

                return approvalType === "approve"
                  ? `You are about to approve an expense of ZMW ${parseFloat(
                      selectedExpense.amount || 0
                    ).toLocaleString()} submitted by ${submittedBy}. The amount will be deducted from the selected account balance.`
                  : `You are about to reject an expense of ZMW ${parseFloat(
                      selectedExpense.amount || 0
                    ).toLocaleString()} submitted by ${submittedBy}. Please provide a reason for rejection.`;
              })()}
              type={approvalType === "approve" ? "info" : "warning"}
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Submitted By">
                {(() => {
                  const userName = selectedExpense.user_created
                    ? `${selectedExpense.user_created.first_name || ""} ${selectedExpense.user_created.last_name || ""}`.trim()
                    : "";
                  return (
                    userName || selectedExpense.created_by || "Unknown User"
                  );
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Amount">
                <Text strong style={{ color: ZAMBIAN_COLORS.red }}>
                  ZMW {parseFloat(selectedExpense.amount || 0).toLocaleString()}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Account">
                {(() => {
                  const account = selectedExpense.account;
                  if (account?.name) return account.name;
                  if (
                    typeof account === "string" ||
                    typeof account === "number"
                  ) {
                    const foundAccount = accounts?.find(
                      (acc) => acc.id === account
                    );
                    return foundAccount?.name || "Unknown Account";
                  }
                  return "Unknown Account";
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Reference">
                {selectedExpense.reference_number || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Notes">
                {selectedExpense.notes || "No additional notes"}
              </Descriptions.Item>
            </Descriptions>

            {approvalType === "reject" && (
              <>
                <Divider />
                <div>
                  <Text strong style={{ marginBottom: 8, display: "block" }}>
                    Reason for Rejection *
                  </Text>
                  <TextArea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Please provide a clear reason for rejecting this expense..."
                    rows={3}
                    maxLength={500}
                    showCount
                  />
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ExpenseApprovalsPage;
